import React from "react";
import styled from "styled-components";
import { Spinner } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import TitleSection from "../TitleSection/TitleSection";
import MenuCategory from "./MenuCategory";
import eatAndPlayConfig from "../../config/eatAndPlayConfig";
import { useEatAndPlayMenu } from "src/hooks/useEatAndPlayMenu";

const TabContent = ({ categoryKey, t, payloadMenu = null }) => {
  const { i18n } = useTranslation();
  const { menu: fetchedMenu, loading, source } = useEatAndPlayMenu({
    categoryKey,
    locale: i18n.language,
    skip: Boolean(payloadMenu),
  });
  const menu = payloadMenu || fetchedMenu;

  if (!payloadMenu && loading) {
    return (
      <container-fluid>
        <LoadingWrap>
          <Spinner animation="border" variant="light" role="status">
            <span className="visually-hidden">{t("loading")}</span>
          </Spinner>
        </LoadingWrap>
      </container-fluid>
    );
  }

  if (menu?.fromPayload) {
    return (
      <container-fluid>
        <TitleSection
          as="h2"
          title={menu.heading}
          text={menu.description || ""}
          className="pt-2"
        />
        <MenuGrid>
          {menu.sectionOrder.map((sectionKey) => (
            <MenuGridItem key={sectionKey}>
              <MenuCategory
                menuSection={sectionKey}
                menuType={categoryKey}
                t={t}
                config={menu}
              />
            </MenuGridItem>
          ))}
        </MenuGrid>
      </container-fluid>
    );
  }

  // Payload is source of truth when the flag is on — do not show stale static menus.
  if (!payloadMenu && source === "payload") {
    return (
      <container-fluid>
        <UnavailableWrap role="status">
          {t("eatAndPlay.unavailable")}
        </UnavailableWrap>
      </container-fluid>
    );
  }

  const categoryHeading = `eatAndPlay.${categoryKey}.heading`;
  const categoryDescription = `eatAndPlay.${categoryKey}.description`;
  const configSection = categoryKey.includes("Breakfast") ? "breakfast" : "main";
  const menuConfig = {
    ...eatAndPlayConfig[configSection],
    extras: eatAndPlayConfig.extras,
  };

  return (
    <container-fluid>
      <TitleSection
        as={"h2"}
        title={t(categoryHeading)}
        text={t(categoryDescription)}
        className="pt-2"
      />
      <MenuGrid>
        {Object.keys(eatAndPlayConfig[configSection]).map((sectionKey) => (
          <MenuGridItem key={sectionKey}>
            <MenuCategory
              menuSection={sectionKey}
              menuType={categoryKey}
              t={t}
              config={menuConfig}
            />
          </MenuGridItem>
        ))}
      </MenuGrid>
    </container-fluid>
  );
};

const MenuGrid = styled.div`
  column-count: 3;
  column-gap: 1.5rem;

  @media screen and (max-width: 991px) {
    column-count: 2;
  }

  @media screen and (max-width: 767px) {
    column-count: 1;
  }
`;

const MenuGridItem = styled.div`
  display: inline-block;
  width: 100%;
  break-inside: avoid;
`;

const LoadingWrap = styled.div`
  display: flex;
  justify-content: center;
  padding: 3rem 1rem;
`;

const UnavailableWrap = styled.p`
  text-align: center;
  color: var(--white);
  padding: 3rem 1rem;
  margin: 0;
  opacity: 0.85;
`;

export default TabContent;
